import { useMutation } from "@apollo/client";
import type {
  Organization,
  UpdateOrganizationBySlugInput,
  UpdateOrganizationPayload,
  OrganizationPatch,
} from "@casey/db-types";
import {
  OrganizationContext,
  normalizeOrganizationContext,
  DEFAULT_ORGANIZATION_CONTEXT,
} from "@casey/db-types";
import { UPDATE_ORGANIZATION_CONTEXT, GET_ORGANIZATION_BY_SLUG } from "../gql";

interface UpdateOrganizationContextVariables {
  input: UpdateOrganizationBySlugInput;
}

interface UpdateOrganizationContextResponse {
  updateOrganizationBySlug: UpdateOrganizationPayload;
}

export interface UseOrganizationContextResult {
  updateOrganizationContext: (
    organizationSlug: string,
    context: OrganizationContext,
  ) => Promise<Organization | null>;
  loading: boolean;
  error: Error | null;
}

export function useOrganizationContext(): UseOrganizationContextResult {
  const [updateOrganizationContextMutation, { loading, error }] = useMutation<
    UpdateOrganizationContextResponse,
    UpdateOrganizationContextVariables
  >(UPDATE_ORGANIZATION_CONTEXT, {
    errorPolicy: "all",
    onError: (error) => {
      console.error("Organization context update error:", error);
    },
  });

  const updateOrganizationContext = async (
    organizationSlug: string,
    context: OrganizationContext,
  ): Promise<Organization | null> => {
    try {
      // Normalize and validate the context
      const normalizedContext = normalizeOrganizationContext(context);

      const organizationPatch: OrganizationPatch = {
        context: JSON.stringify(normalizedContext),
      };

      const result = await updateOrganizationContextMutation({
        variables: {
          input: {
            slug: organizationSlug,
            patch: organizationPatch,
          },
        },
        // Refetch the organization data to get updated context
        refetchQueries: [
          {
            query: GET_ORGANIZATION_BY_SLUG,
            variables: { organizationSlug },
          },
        ],
      });

      const updatedOrganization =
        result.data?.updateOrganizationBySlug?.organization;

      return updatedOrganization || null;
    } catch (err) {
      console.error("Error updating organization context:", err);
      throw err;
    }
  };

  return {
    updateOrganizationContext,
    loading,
    error: error || null,
  };
}

// Helper hook to get organization context from organization data
export function useOrganizationContextData(
  organization: Organization | null,
): OrganizationContext {
  if (!organization?.context) {
    return DEFAULT_ORGANIZATION_CONTEXT;
  }

  try {
    const parsed =
      typeof organization.context === "string"
        ? JSON.parse(organization.context)
        : organization.context;
    return normalizeOrganizationContext(parsed);
  } catch (error) {
    console.error("Error parsing organization context:", error);
    return DEFAULT_ORGANIZATION_CONTEXT;
  }
}
